import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import axios from 'axios';
import logo from '../../assets/logo.png';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser, isEmailVerified } = useAuth();
  
  const [status, setStatus] = useState('loading'); // loading, success, error, no-token
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');
  const nextUrl = searchParams.get('next'); // Support redirect after verification

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('no-token');
        return;
      }

      try {
        const response = await axios.post(`${API_URL}/api/auth/verify-email?token=${token}`);
        setStatus('success');
        setMessage(response.data.message);
        
        // Refresh user data if logged in
        if (user) {
          await refreshUser();
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Verification failed. The link may be expired or invalid.');
      }
    };

    verifyEmail();
  }, [token, user, refreshUser]);

  // If already verified and logged in, redirect
  useEffect(() => {
    if (isEmailVerified && status === 'success') {
      const timer = setTimeout(() => {
        // Redirect to next URL if provided, otherwise appropriate dashboard
        const redirectTo = nextUrl || (user?.role === 'host' ? '/app/host' : '/app');
        navigate(redirectTo, { replace: true });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isEmailVerified, status, navigate, user, nextUrl]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          {/* Logo */}
          <Link to="/" className="inline-block mb-6">
            <img src={logo} alt="Southwest FL Vacation Rentals" className="h-16 w-auto mx-auto" />
          </Link>

          {status === 'loading' && (
            <>
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Verifying your email...
              </h1>
              <p className="text-slate-500">
                Please wait while we verify your email address.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Email Verified!
              </h1>
              <p className="text-slate-500 mb-6">
                {message || 'Your email has been successfully verified. You now have full access to all features.'}
              </p>
              {user ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-500">Redirecting you automatically...</p>
                  <Button asChild className="w-full">
                    <Link to={user.role === 'host' ? '/app/host' : '/app'}>
                      Go to Dashboard
                    </Link>
                  </Button>
                </div>
              ) : (
                <Button asChild className="w-full">
                  <Link to="/login">Sign In</Link>
                </Button>
              )}
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Verification Failed
              </h1>
              <p className="text-slate-500 mb-6">
                {message}
              </p>
              <div className="space-y-3">
                {user && !isEmailVerified && (
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/app">
                      Request New Verification Email
                    </Link>
                  </Button>
                )}
                <Button asChild className="w-full">
                  <Link to="/">Go to Homepage</Link>
                </Button>
              </div>
            </>
          )}

          {status === 'no-token' && (
            <>
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Email Verification
              </h1>
              <p className="text-slate-500 mb-6">
                No verification token found. Please click the link in your verification email, or request a new one.
              </p>
              <div className="space-y-3">
                {user ? (
                  <Button asChild className="w-full">
                    <Link to="/app">Go to Dashboard</Link>
                  </Button>
                ) : (
                  <Button asChild className="w-full">
                    <Link to="/login">Sign In</Link>
                  </Button>
                )}
              </div>
            </>
          )}
        </div>

        <p className="text-center text-slate-500 text-sm mt-6">
          Need help?{' '}
          <a href="mailto:support@southwestflvacationrentals.com" className="text-primary hover:underline">
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
}
